import React, { useState, useEffect } from 'react';
import levenshtein from 'fast-levenshtein';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';
import { parseMultipleChoiceBulk, ParsedMCQ } from '../../../utils/parseMultipleChoiceBulk';
import { questionService } from '../../../services/question.service';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #007bff;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9ff, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #007bff;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const OptionRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const OptionBadge = styled.span`
  background: #007bff;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 35px;
  text-align: center;
`;

interface MultipleChoiceFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const MultipleChoiceForm: React.FC<MultipleChoiceFormProps> = ({ formData, onChange }) => {
  // Bulk input modal state
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<string[]>([]);
  const [bulkSkip, setBulkSkip] = useState<boolean[]>([]); // skip flags
  const [compareIdx, setCompareIdx] = useState<number|null>(null); // index để so sánh popup
  const parseContent = () => {
    try {
      return typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : {
            text: '',
            options: ['', '', '', ''],
            correct_index: 0,
          };
    } catch {
      return {
        text: '',
        options: ['', '', '', ''],
        correct_index: 0,
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleTextChange = (value: string) => {
    setContentData({
      ...contentData,
      text: value,
    });
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...contentData.options];
    newOptions[index] = value;
    setContentData({
      ...contentData,
      options: newOptions,
    });
  };

  const handleCorrectIndexChange = (index: number) => {
    setContentData({
      ...contentData,
      correct_index: index,
    });
  };

  const optionLabels = ['A', 'B', 'C', 'D'];

  // Handle bulk parse
  // Lấy danh sách câu hỏi hiện có (gọi 1 lần khi mở modal parse)
  const [allQuestions, setAllQuestions] = useState<string[]>([]);
  useEffect(() => {
    if (bulkModalOpen && allQuestions.length === 0) {
      questionService.getAllPaginated({ page: 1, limit: 1000 }).then(res => {
        const arr = (res?.data || res?.questions || []).map((q: any) => {
          try {
            const c = typeof q.content === 'string' ? JSON.parse(q.content) : q.content;
            return c?.text || '';
          } catch { return ''; }
        });
        setAllQuestions(arr);
      });
    }
  }, [bulkModalOpen]);

  // Hàm normalize chuỗi
  const normalize = (s: string) => s.trim().toLowerCase().replace(/[?.!]/g, '');
  // Kiểm tra trùng/gần trùng (Levenshtein), trả về index câu nghi trùng hoặc -1
  const findDuplicateIdx = (q: string) => {
    const nq = normalize(q);
    for (let i = 0; i < allQuestions.length; i++) {
      const ne = normalize(allQuestions[i]);
      if (ne === nq) return i;
      const maxLen = Math.max(ne.length, nq.length);
      if (maxLen > 0 && levenshtein.get(ne, nq) / maxLen < 0.15) return i;
    }
    return -1;
  };

  // Phân tích real-time khi sửa bulkText
  const bulkParsed = parseMultipleChoiceBulk(bulkText);
  // State cho từng câu đã chỉnh sửa (cả text và đáp án)
  const [bulkEdit, setBulkEdit] = useState<{question: string, choices: string[], correct_index?: number}[]>([]);
  // Khi số lượng câu thay đổi (do sửa bulkText), đồng bộ lại bulkEdit
  useEffect(() => {
    setBulkEdit(prev => {
      if (bulkParsed.length === prev.length) return prev;
      if (bulkParsed.length > prev.length) {
        return [
          ...prev,
          ...bulkParsed.slice(prev.length).map(q => ({ question: q.question, choices: [...q.choices], correct_index: 0 }))
        ];
      }
      return prev.slice(0, bulkParsed.length);
    });
    // eslint-disable-next-line
  }, [bulkText]);
  const bulkEditDisplay = bulkParsed.map((q, i) => {
    if (bulkEdit[i] !== undefined) return bulkEdit[i];
    return { question: q.question, choices: [...q.choices], correct_index: 0 };
  });
  const bulkDupIdx = bulkEditDisplay.map(q => findDuplicateIdx(q.question));

  // Handle confirm bulk import
  // Lưu hàng loạt câu hỏi
  const handleBulkSaveAll = async () => {
    if (!bulkParsed.length) return;
    setBulkLoading(true);
    setBulkResult([]);
    const results: string[] = [];
    for (let i = 0; i < bulkParsed.length; i++) {
      const q = bulkParsed[i];
      // Tận dụng các trường chung từ formData
      const newQuestion = {
        ...formData,
        content: JSON.stringify({
          text: q.question,
          options: q.choices,
          correct_index: 0,
        })
      };
      try {
        await questionService.create(newQuestion);
        results.push(`✅ Câu ${i+1}: Thành công`);
      } catch (err) {
        results.push(`❌ Câu ${i+1}: Lỗi`);
      }
    }
    setBulkResult(results);
    setBulkLoading(false);
  };

  // Dùng câu đầu tiên cho form hiện tại
  const handleBulkConfirm = () => {
    if (bulkParsed.length > 0) {
      const first = bulkParsed[0];
      setContentData({
        text: first.question,
        options: first.choices,
        correct_index: 0,
      });
    }
    setBulkModalOpen(false);
    setBulkText('');
    setBulkSkip([]);
  };

  return (
    <FormBox>
      <h5>📝 Nội dung câu hỏi - Trắc nghiệm A, B, C, D</h5>
      
      <FormGroup>
        <Button color="primary" outline size="sm" style={{ float: 'right', marginBottom: 8 }} onClick={() => setBulkModalOpen(true)}>
          Nhập nhiều câu hỏi
        </Button>
        <Label>Câu hỏi: *</Label>
        <Input
          type="textarea"
          value={contentData.text || ''}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder="Nhập câu hỏi... Ví dụ: Which planet is known as the Red Planet?"
          rows={3}
        />
      </FormGroup>

      <FormGroup>
        <Label>Các đáp án:</Label>
        {(contentData.options || ['', '', '', '']).map((option: string, index: number) => (
          <OptionRow key={index}>
            <OptionBadge>{optionLabels[index]}</OptionBadge>
            <Input
              type="text"
              value={option}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              placeholder={`Đáp án ${optionLabels[index]}...`}
              style={{ flex: 1 }}
            />
            <RadioLabel>
              <input
                type="radio"
                name="correct_answer"
                checked={contentData.correct_index === index}
                onChange={() => handleCorrectIndexChange(index)}
              />
              <span style={{ fontSize: '14px', color: '#666' }}>Đúng</span>
            </RadioLabel>
          </OptionRow>
        ))}
        <small className="form-text text-muted">
          ✓ Chọn radio button bên phải để đánh dấu đáp án đúng
        </small>
      </FormGroup>

      {/* Preview JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    {/* Bulk input modal */}
    <Modal isOpen={bulkModalOpen} toggle={() => setBulkModalOpen(false)} size="lg">
      <ModalHeader toggle={() => setBulkModalOpen(false)}>
        Nhập nhiều câu hỏi trắc nghiệm
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Nhập đoạn text gồm nhiều câu hỏi trắc nghiệm (mỗi câu bắt đầu bằng số thứ tự, đáp án bắt đầu bằng A. B. C. D.)</Label>
          <Input
            type="textarea"
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            rows={12}
            placeholder={"13. The town is overcrowded...\nA. health\tB. education\tC. amenities\tD. places\n..."}
          />
        </FormGroup>
        <div style={{ marginTop: 16 }}>
          <h6>Kết quả phân tích ({bulkParsed.length} câu):</h6>
          {bulkParsed.map((q, idx) => {
            const dupIdx = bulkDupIdx[idx];
            const edit = bulkEditDisplay[idx];
            return (
              <FormBox key={idx} style={{ background: bulkSkip[idx] ? '#f8d7da' : (dupIdx !== -1 ? '#fff3cd' : '#f6faff'), borderColor: dupIdx !== -1 ? '#ffc107' : (bulkSkip[idx] ? '#dc3545' : '#aaa'), marginBottom: 10, opacity: bulkSkip[idx] ? 0.5 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <b>Câu {idx+1}:</b>
                  <Input
                    type="textarea"
                    value={edit.question}
                    onChange={e => {
                      const arr = [...bulkEdit];
                      arr[idx] = { ...edit, question: e.target.value };
                      setBulkEdit(arr);
                    }}
                    rows={2}
                    style={{ flex: 1, background: dupIdx !== -1 ? '#fffbe6' : undefined }}
                  />
                  {dupIdx !== -1 && (
                    <Button color="warning" size="sm" outline onClick={() => setCompareIdx(idx)} style={{ marginRight: 8 }}>So sánh</Button>
                  )}
                  {dupIdx !== -1 && (
                    <Button color={bulkSkip[idx] ? 'secondary' : 'danger'} size="sm" outline onClick={() => {
                      const arr = [...bulkSkip]; arr[idx] = !arr[idx]; setBulkSkip(arr);
                    }}>{bulkSkip[idx] ? 'Bỏ qua' : 'Loại'}</Button>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Label style={{ fontWeight: 500 }}>Các đáp án:</Label>
                  {edit.choices.map((c, i) => (
                    <OptionRow key={i}>
                      <OptionBadge>{String.fromCharCode(65+i)}</OptionBadge>
                      <Input
                        type="text"
                        value={c}
                        onChange={e => {
                          const arr = [...bulkEdit];
                          const newChoices = [...edit.choices];
                          newChoices[i] = e.target.value;
                          arr[idx] = { ...edit, choices: newChoices };
                          setBulkEdit(arr);
                        }}
                        placeholder={`Đáp án ${String.fromCharCode(65+i)}...`}
                        style={{ flex: 1 }}
                      />
                      <RadioLabel>
                        <input
                          type="radio"
                          name={`correct_answer_${idx}`}
                          checked={edit.correct_index === i}
                          onChange={() => {
                            const arr = [...bulkEdit];
                            arr[idx] = { ...edit, correct_index: i };
                            setBulkEdit(arr);
                          }}
                        />
                        <span style={{ fontSize: '14px', color: '#666' }}>Đúng</span>
                      </RadioLabel>
                    </OptionRow>
                  ))}
                </div>
                {dupIdx !== -1 && (
                  <div style={{ color: '#856404', background: '#fff3cd', padding: 4, borderRadius: 4, marginTop: 4, fontSize: 13 }}>
                    ⚠️ Câu này trùng hoặc gần trùng với câu đã có!
                  </div>
                )}
              </FormBox>
            );
          })}
        </div>
        <div style={{ marginTop: 12 }}>
          <Button color="success" onClick={() => {
            // Dùng câu đầu tiên
            if (bulkParsed.length > 0) {
              setContentData({
                text: bulkEditDisplay[0].question,
                options: bulkEditDisplay[0].choices,
                correct_index: bulkEditDisplay[0].correct_index ?? 0,
              });
            }
            setBulkModalOpen(false);
            setBulkText('');
            setBulkSkip([]);
          }} style={{ marginRight: 8 }}>Dùng câu đầu tiên</Button>
          <Button color="primary" onClick={async () => {
            setBulkLoading(true);
            setBulkResult([]);
            const results: string[] = [];
            for (let i = 0; i < bulkParsed.length; i++) {
              if (bulkSkip[i]) { results.push(`⏭️ Câu ${i+1}: Bỏ qua`); continue; }
              const q = bulkParsed[i];
              const newQuestion = {
                ...formData,
                content: JSON.stringify({
                  text: bulkEditDisplay[i].question,
                  options: bulkEditDisplay[i].choices,
                  correct_index: bulkEditDisplay[i].correct_index ?? 0,
                })
              };
              try {
                await questionService.create(newQuestion);
                results.push(`✅ Câu ${i+1}: Thành công`);
              } catch (err) {
                results.push(`❌ Câu ${i+1}: Lỗi`);
              }
            }
            setBulkResult(results);
            setBulkLoading(false);
          }} disabled={bulkLoading} style={{ marginRight: 8 }}>
            {bulkLoading ? 'Đang lưu...' : 'Lưu tất cả'}
          </Button>
        </div>
        {bulkResult.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h6>Kết quả lưu:</h6>
            <ul>
              {bulkResult.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {/* Popup so sánh 2 câu nghi trùng */}
        {compareIdx !== null && bulkDupIdx[compareIdx] !== -1 && (
          <Modal isOpen toggle={() => setCompareIdx(null)}>
            <ModalHeader toggle={() => setCompareIdx(null)}>So sánh câu nghi trùng</ModalHeader>
            <ModalBody>
              <div style={{ marginBottom: 12 }}>
                <b>Câu mới:</b>
                <div style={{ background: '#e3f2fd', padding: 8, borderRadius: 4 }}>
                  <div><b>Q:</b> {bulkEdit[compareIdx]?.question}</div>
                  {bulkEdit[compareIdx]?.choices?.map((c, i) => (
                    <div key={i}><b>{String.fromCharCode(65+i)}.</b> {c}</div>
                  ))}
                </div>
              </div>
              <div>
                <b>Câu đã có:</b>
                <div style={{ background: '#fffbe6', padding: 8, borderRadius: 4 }}>
                  <div><b>Q:</b> {allQuestions[bulkDupIdx[compareIdx]]}</div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setCompareIdx(null)}>Đóng</Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setBulkModalOpen(false)}>Đóng</Button>
      </ModalFooter>
    </Modal>
    </FormBox>
  );
};

export default MultipleChoiceForm;
